import React from 'react';
import { Package, Search as SearchIcon } from 'lucide-react';

const ProductSelector = ({ menuData, selectedList, onUpdate, query, t }) => {

    const getFilteredProducts = (queryText, returnGrouped = false) => {
        if (!menuData?.categories) return [];
        const q = queryText.toLowerCase();

        if (!q && returnGrouped) return menuData.categories;

        let allItems = [];
        menuData.categories.forEach(cat => {
            if (Array.isArray(cat.items)) {
                cat.items.forEach(item => {
                    allItems.push({ ...item, categoryName: cat.categoryName });
                });
            }
        });
        return allItems.filter(item => item.name.toLowerCase().includes(q));
    };

    const formatPrice = (price) => {
        if (!price && price !== 0) return null;
        return `${parseFloat(price).toFixed(2)} ₺`;
    };

    const renderProductGrid = (items) => (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '8px', paddingRight: '4px' }}>
            {items.map(item => {
                const isSelected = selectedList?.includes(item.name);
                const hasImage = item.imageUrl || item.image;
                return (
                    <label key={item.name} style={{
                        display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                        background: isSelected ? 'rgba(var(--primary-rgb), 0.06)' : 'var(--bg-card)',
                        borderRadius: '12px',
                        border: isSelected ? '2px solid var(--primary)' : '2px solid transparent',
                        boxShadow: isSelected ? '0 2px 8px rgba(var(--primary-rgb), 0.12)' : '0 1px 3px rgba(0,0,0,0.04)',
                        cursor: 'pointer', transition: 'all 0.15s ease',
                        outline: '1px solid var(--border)',
                    }}>
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={e => {
                                const newSelected = e.target.checked
                                    ? [...(selectedList || []), item.name]
                                    : (selectedList || []).filter(n => n !== item.name);
                                onUpdate(newSelected);
                            }}
                            style={{ display: 'none' }}
                        />
                        {hasImage ? (
                            <img
                                src={item.imageUrl || item.image}
                                alt={item.name}
                                style={{
                                    width: '36px', height: '36px', borderRadius: '8px',
                                    objectFit: 'cover', flexShrink: 0,
                                    border: isSelected ? '1px solid var(--primary)' : '1px solid var(--border)',
                                }}
                            />
                        ) : (
                            <div style={{
                                width: '36px', height: '36px', borderRadius: '8px', flexShrink: 0,
                                background: isSelected ? 'rgba(var(--primary-rgb), 0.15)' : 'var(--bg-main)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: isSelected ? '1px solid var(--primary)' : '1px solid var(--border)',
                            }}>
                                <Package size={16} style={{ color: isSelected ? 'var(--primary)' : 'var(--text-muted)', opacity: 0.6 }} />
                            </div>
                        )}
                        <div style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
                            <span style={{
                                fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden',
                                textOverflow: 'ellipsis', display: 'block',
                                fontWeight: isSelected ? 700 : 500,
                                color: isSelected ? 'var(--primary)' : 'var(--text-main)'
                            }}>{item.name}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {item.categoryName && (
                                    <span style={{
                                        fontSize: '0.65rem',
                                        color: isSelected ? 'var(--primary)' : 'var(--text-muted)',
                                        opacity: 0.8
                                    }}>{item.categoryName}</span>
                                )}
                                {item.price != null && (
                                    <span style={{
                                        fontSize: '0.65rem', fontWeight: 700,
                                        color: isSelected ? 'var(--primary)' : 'var(--text-muted)',
                                    }}>{formatPrice(item.price)}</span>
                                )}
                            </div>
                        </div>
                        <div style={{
                            width: '20px', height: '20px', borderRadius: '6px', flexShrink: 0,
                            border: isSelected ? '2px solid var(--primary)' : '2px solid var(--border)',
                            background: isSelected ? 'var(--primary)' : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.15s ease',
                        }}>
                            {isSelected && (
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </div>
                    </label>
                );
            })}
            {(!items || items.length === 0) && (
                <div style={{
                    gridColumn: '1 / -1', padding: '2rem', textAlign: 'center',
                    color: 'var(--text-muted)', fontSize: '0.875rem',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                }}>
                    <SearchIcon size={24} style={{ opacity: 0.3 }} />
                    <span>Ürün bulunamadı.</span>
                </div>
            )}
        </div>
    );

    const totalProducts = menuData?.categories?.reduce((acc, cat) => acc + (cat.items?.length || 0), 0) || 0;
    const selectedCount = selectedList?.length || 0;

    return (
        <div>
            {/* Selection summary */}
            {selectedCount > 0 && (
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', marginBottom: '12px',
                    background: 'rgba(var(--primary-rgb), 0.06)', borderRadius: '10px',
                    border: '1px solid rgba(var(--primary-rgb), 0.15)',
                }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--primary)' }}>
                        {selectedCount} / {totalProducts} ürün seçildi
                    </span>
                    <button
                        type="button"
                        onClick={() => onUpdate([])}
                        style={{
                            background: 'none', border: 'none', color: 'var(--primary)',
                            fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                            padding: '2px 8px', borderRadius: '6px',
                            transition: 'background 0.15s',
                        }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(var(--primary-rgb), 0.1)'}
                        onMouseOut={e => e.currentTarget.style.background = 'none'}
                    >
                        Temizle
                    </button>
                </div>
            )}

            <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                {!query ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {getFilteredProducts('', true).map(cat => {
                            const catItems = Array.isArray(cat.items) ? cat.items : [];
                            const allSelected = catItems.length > 0 && catItems.every(i => selectedList?.includes(i.name));
                            const someSelected = catItems.some(i => selectedList?.includes(i.name));
                            const catSelectedCount = catItems.filter(i => selectedList?.includes(i.name)).length;

                            const toggleCategory = (checked) => {
                                const itemNames = catItems.map(i => i.name);
                                let newSelected = [...(selectedList || [])];
                                if (checked) {
                                    itemNames.forEach(name => {
                                        if (!newSelected.includes(name)) newSelected.push(name);
                                    });
                                } else {
                                    newSelected = newSelected.filter(name => !itemNames.includes(name));
                                }
                                onUpdate(newSelected);
                            };

                            return (
                                <div key={cat.categoryName}>
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: '10px',
                                        marginBottom: '0.8rem', paddingBottom: '0.5rem',
                                        borderBottom: '1px solid var(--border)',
                                        position: 'sticky', top: 0, background: 'var(--bg-sidebar)', zIndex: 10,
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            ref={el => el && (el.indeterminate = someSelected && !allSelected)}
                                            onChange={e => toggleCategory(e.target.checked)}
                                            style={{ width: '16px', height: '16px', accentColor: 'var(--primary)', cursor: 'pointer' }}
                                        />
                                        <span style={{ fontWeight: 800, fontSize: '0.9rem', color: 'var(--text-main)' }}>{cat.categoryName}</span>
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>({catItems.length} ürün)</span>
                                        {catSelectedCount > 0 && (
                                            <span style={{
                                                fontSize: '0.65rem', fontWeight: 700, color: 'white',
                                                background: 'var(--primary)', borderRadius: '10px',
                                                padding: '1px 8px', marginLeft: 'auto',
                                            }}>
                                                {catSelectedCount} seçili
                                            </span>
                                        )}
                                    </div>
                                    {renderProductGrid(catItems.map(i => ({ ...i, categoryName: cat.categoryName })))}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    renderProductGrid(getFilteredProducts(query))
                )}
            </div>
        </div>
    );
};

export default ProductSelector;